/**
 * Events — migrated from mockData to live svdy-platform API.
 *
 * Backend shape: EventResponse = { id, title, event_date, event_type,
 * description, all_day, end_date, created_at, created_by }. Notably
 * simpler than the mock (no status, no attendees, no location, no
 * start/end time) — we lean on event_date + end_date + all_day for
 * range semantics.
 *
 * event_type is a free-form string; the form offers a Select with
 * common values (meeting, training, social, announcement, holiday,
 * other) but the backend accepts anything.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import AudienceScopeField from '../../../components/organization/AudienceScopeField';

import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
type Event = Schema<'EventResponse'>;
type EventCreate = Schema<'EventCreate'>;
type EventUpdate = Schema<'EventUpdate'>;

type FormState = Partial<Event>;

const EVENT_TYPES = [
  { value: 'meeting', color: 'info' as const },
  { value: 'training', color: 'warning' as const },
  { value: 'social', color: 'success' as const },
  { value: 'announcement', color: 'secondary' as const },
  { value: 'holiday', color: 'primary' as const },
  { value: 'other', color: 'default' as const },
];

function typeColor(
  t: string | null | undefined,
): 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' {
  const hit = EVENT_TYPES.find((et) => et.value === t);
  return hit?.color ?? 'default';
}

/** Classify an event by its date for the summary KPIs. */
function isUpcoming(e: Event): boolean {
  if (!e.event_date) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return new Date(e.event_date) >= now;
}

const Events: React.FC = () => {
  const { t } = useTranslation();

  // Translated label for an event_type value.
  const typeLabel = (et: string | null | undefined): string => {
    if (!et) return '-';
    return t(`org:events.type.${et}`, et);
  };

  const [rows, setRows] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [current, setCurrent] = useState<FormState | null>(null);
  const [toDelete, setToDelete] = useState<Event | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationApi.eventsApi.list();
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('org:events.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // BUG-3 fix (2026-07-07): deep-link support — /organization/events?id=<id>
  // opens that event's detail dialog. Used by the notification bell
  // click-through (org/service.py fanout_to_org_workers). Mirrors the
  // SystemSupport.tsx ?case_id= pattern.
  const [searchParams, setSearchParams] = useSearchParams();
  const openedIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    const fromUrl = searchParams.get('id');
    if (!fromUrl || openedIdRef.current === fromUrl || rows.length === 0) return;
    const row = rows.find((ev) => ev.id === fromUrl);
    if (row) {
      openedIdRef.current = fromUrl;
      handleOpenEditDialog(row);
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, rows, setSearchParams]);

  const summaryStats = useMemo(() => {
    const total = rows.length;
    const upcoming = rows.filter(isUpcoming).length;
    const past = total - upcoming;
    const allDay = rows.filter((e) => e.all_day !== false).length;
    return { total, upcoming, past, allDay };
  }, [rows]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:events.stats.total'),
        description: t('org:events.stats.totalDescription'),
        avatar: {
          icon: <EventIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'upcoming',
        value: summaryStats.upcoming,
        title: t('org:events.stats.upcoming'),
        description: t('org:events.stats.upcomingDescription'),
        avatar: {
          icon: <PendingActionsOutlinedIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
      {
        id: 'past',
        value: summaryStats.past,
        title: t('org:events.stats.past'),
        description: t('org:events.stats.pastDescription'),
        avatar: {
          icon: <EventAvailableOutlinedIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'allday',
        value: summaryStats.allDay,
        title: t('org:events.stats.allDay'),
        description: t('org:events.stats.allDayDescription'),
        avatar: {
          icon: <EventIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
    ],
    [summaryStats, t],
  );

  const filtered = useMemo(() => {
    const scoped = typeFilter === 'all'
      ? rows
      : rows.filter((e) => (e.event_type ?? '') === typeFilter);
    const needle = filterText.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter(
      (e) =>
        e.title.toLowerCase().includes(needle) ||
        (e.description ?? '').toLowerCase().includes(needle) ||
        (e.event_type ?? '').toLowerCase().includes(needle),
    );
  }, [rows, filterText, typeFilter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // -------------------------------------------------------------------------
  // CRUD
  // -------------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!current) return false;
    const errs: Record<string, string> = {};
    if (!current.title?.trim()) errs.title = t('org:events.fields.titleRequired');
    if (!current.event_date) errs.event_date = t('org:events.fields.eventDateRequired');
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    setCurrent({ all_day: false, event_type: 'meeting', start_time: '09:00', end_time: '10:00' });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (ev: Event) => {
    setCurrent({ ...ev });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrent(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    if (!validateForm() || !current) return;
    setSaving(true);
    try {
      if (current.id) {
        const update: EventUpdate = {
          title: current.title ?? null,
          description: current.description ?? null,
          event_date: current.event_date ?? null,
          end_date: current.end_date ?? null,
          event_type: current.event_type ?? null,
          all_day: current.all_day ?? null,
          start_time: current.all_day ? null : (current.start_time ?? null),
          end_time: current.all_day ? null : (current.end_time ?? null),
          audience_scope: current.audience_scope ?? 'all',
          audience_department_ids: current.audience_department_ids ?? [],
        };
        await organizationApi.eventsApi.update(current.id, update);
        setToast(t('org:events.toast.updated', { name: current.title }));
      } else {
        const create: EventCreate = {
          title: current.title!,
          event_date: current.event_date!,
          description: current.description ?? null,
          end_date: current.end_date ?? null,
          event_type: current.event_type ?? null,
          all_day: current.all_day ?? false,
          start_time: current.all_day ? null : (current.start_time ?? null),
          end_time: current.all_day ? null : (current.end_time ?? null),
          audience_scope: current.audience_scope ?? 'all',
          audience_department_ids: current.audience_department_ids ?? [],
        };
        await organizationApi.eventsApi.create(create);
        setToast(t('org:events.toast.created', { name: create.title }));
      }
      handleCloseFormDialog();
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:events.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (ev: Event) => {
    setToDelete(ev);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await organizationApi.eventsApi.delete(toDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== toDelete.id));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      setToast(t('org:events.toast.deleted', { name: toDelete.title }));
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:events.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:events.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.eventsApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:events.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:events.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setCurrent((prev) =>
      prev
        ? {
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
          }
        : null,
    );
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrent((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  // -------------------------------------------------------------------------
  // Row selection / pagination
  // -------------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((r) => r.id) : []);
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:events.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:events.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:events.addButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('org:events.filters.typeLabel')}</InputLabel>
          <Select
            value={typeFilter}
            label={t('org:events.filters.typeLabel')}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:events.filters.allTypes')}</MenuItem>
            {EVENT_TYPES.map((et) => (
              <MenuItem key={et.value} value={et.value}>{typeLabel(et.value)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          label={t('org:events.searchLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 340, maxWidth: '100%' }}
        />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Chips §5 — active non-default filters, individually clearable. */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (typeFilter !== 'all') {
          chips.push({
            key: 'type',
            label: t('org:events.filters.typeChip', { label: typeLabel(typeFilter) }),
            onClear: () => { setTypeFilter('all'); setPage(0); },
          });
        }
        if (filterText.trim()) {
          chips.push({
            key: 'search',
            label: t('org:events.filters.searchChip', { query: filterText.trim() }),
            onClear: () => { setFilterText(''); setPage(0); },
          });
        }
        if (chips.length === 0) return null;
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {chips.map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                onDelete={c.onClear}
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setTypeFilter('all');
                setFilterText('');
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'text.secondary',
                minWidth: 0,
                px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}
            >
              {t('org:events.filters.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText || typeFilter !== 'all'
              ? t('org:events.empty.noMatches')
              : t('org:events.empty.noResults')}
          </Typography>
          {!filterText && typeFilter === 'all' && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:events.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:events.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:events.table.title')}</TableCell>
                <TableCell>{t('org:events.table.date')}</TableCell>
                <TableCell>{t('org:events.table.endDate')}</TableCell>
                <TableCell>{t('org:events.table.type')}</TableCell>
                <TableCell>{t('org:events.table.allDay')}</TableCell>
                <TableCell align="center">{t('org:events.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((ev) => {
                const selected = isSelected(ev.id);
                return (
                  <TableRow
                    hover
                    key={ev.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, ev.id)}
                        inputProps={{ 'aria-label': ev.title }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ev.title}
                        </Typography>
                        {ev.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              maxWidth: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {ev.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{ev.event_date}</TableCell>
                    <TableCell>{ev.end_date ?? '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={typeLabel(ev.event_type)}
                        color={typeColor(ev.event_type)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>{ev.all_day !== false ? t('org:events.table.yes') : t('org:events.table.no')}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        aria-label={t('org:shared.table.edit')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(ev);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('common:actions.delete')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(ev);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {current?.id ? t('org:events.editDialogTitle') : t('org:events.addDialogTitle')}
        </DialogTitle>
        <DialogContent>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label={t('org:events.fields.title')}
            fullWidth
            variant="outlined"
            value={current?.title || ''}
            onChange={handleFormChange}
            required
            error={!!formErrors.title}
            helperText={formErrors.title}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label={t('org:events.fields.description')}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={current?.description || ''}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DateField
                margin="dense"
                name="event_date"
                label={t('org:events.fields.eventDate')}
                fullWidth
                variant="outlined"
                value={current?.event_date || ''}
                onChange={handleFormChange}
                required
                error={!!formErrors.event_date}
                helperText={formErrors.event_date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateField
                margin="dense"
                name="end_date"
                label={t('org:events.fields.endDate')}
                fullWidth
                variant="outlined"
                value={current?.end_date || ''}
                onChange={handleFormChange}
              />
            </Grid>
          </Grid>
          <FormControl fullWidth margin="dense" sx={{ mt: 2, mb: 1 }}>
            <InputLabel id="event-type-label">{t('org:events.fields.eventType')}</InputLabel>
            <Select
              labelId="event-type-label"
              name="event_type"
              value={current?.event_type ?? ''}
              label={t('org:events.fields.eventType')}
              onChange={handleSelectChange}
            >
              <MenuItem value="">
                <em>{t('org:shared.form.none')}</em>
              </MenuItem>
              {EVENT_TYPES.map((et) => (
                <MenuItem key={et.value} value={et.value}>
                  {typeLabel(et.value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={!!current?.all_day}
                onChange={handleFormChange}
                name="all_day"
                color="primary"
              />
            }
            label={t('org:events.fields.allDay')}
          />
          {!current?.all_day && (
            <Grid container spacing={2} sx={{ mb: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label={t('org:events.fields.startTime')}
                  type="time"
                  fullWidth
                  variant="outlined"
                  value={current?.start_time ?? '09:00'}
                  onChange={(e) => setCurrent((prev) => ({ ...prev, start_time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label={t('org:events.fields.endTime')}
                  type="time"
                  fullWidth
                  variant="outlined"
                  value={current?.end_time ?? '10:00'}
                  onChange={(e) => setCurrent((prev) => ({ ...prev, end_time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}
          <Box sx={{ mt: 2 }}>
            <AudienceScopeField
              scope={current?.audience_scope ?? 'all'}
              departmentIds={current?.audience_department_ids ?? []}
              onChange={(next) => setCurrent((prev) => ({ ...prev, ...next }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: '16px' }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : current?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:events.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — shared ConfirmDialog primitive. */}
      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:events.deleteTitle')}
        description={t('org:events.deleteDescription')}
        subject={
          toDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {toDelete.title}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events;
